import React, { useEffect, useCallback, useMemo } from "react";
import { longitud } from "../../../../constantes/constantes";
import './arrowsTabs.sass';

/**
 * Formatea la fecha para mostrar en las tabs
 * Retorna un objeto con el día y la fecha separados
 * Reglas:
 * - Si es hoy: muestra "Hoy" en day
 * - Si no es hoy: muestra el nombre del día en day
 * - La fecha se muestra según el año (sin año si es año actual, con año si es otro año)
 * @param {string} dateString - Fecha en formato "dd/mm/yyyy"
 * @param {string} dayName - Nombre del día (Lunes, Martes, etc.)
 * @returns {Object} { day: string, date: string }
 */
const formatTabDate = (dateString, dayName) => {
  if (!dateString) return { day: "Día", date: "Fecha" };
  
  const [dd, mm, yyyy] = dateString.split('/');
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentDay = String(today.getDate()).padStart(2, '0');
  const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
  const currentYearStr = String(today.getFullYear());
  
  const monthNames = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const monthName = monthNames[parseInt(mm) - 1];
  const tabYear = parseInt(yyyy);
  
  // Verificar si es realmente hoy (comparar dd/mm/yyyy)
  const isToday = dd === currentDay && mm === currentMonth && yyyy === currentYearStr;
  
  // Si es hoy, mostrar "Hoy" en day
  if (isToday) {
    return {
      day: "HOY",
      date: `${dd} ${monthName.toUpperCase()}`
    };
  }
  
  // Si no es hoy, mostrar el nombre del día
  // Si es del año actual, no mostrar el año
  if (tabYear === currentYear) {
    return {
      day: dayName.toUpperCase(),
      date: `${dd} ${monthName.toUpperCase()}`
    };
  }
  
  // Si es de otro año (siguiente o anterior), mostrar el año
  return {
    day: dayName,
    date: `${dd} ${monthName} ${yyyy}`
  };
};

// Constantes
const CONSTANTS = {
  MOBILE_BREAKPOINT: 768,
  ANIMATION_DURATION: 150,
  SCROLL_FACTORS: { mobile: 1, desktop: 1 / 3 },
  MAX_TABS_WITHOUT_CAROUSEL: 7
};

/**
 * Componente reutilizable para las flechas de navegación
 */
const ArrowButton = ({ direction, onClick }) => {
  const isLeft = direction === 'left';
  const directionText = isLeft ? 'izquierda' : 'derecha';

  return (
    <div
      id={isLeft ? 'arrowL' : 'arrowR'}
      className={`arrow-button arrow-${direction}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Desplazar tabs hacia la ${directionText}`}
    />
  );
};

/**
 * Componente de navegación por tabs con flechas de desplazamiento
 * Permite navegar entre diferentes días (menos3, menos2, menos1, HOY, mas1, mas2, mas3)
 * Incluye un selector de fecha para cambiar la fecha central
 * 
 * @param {Object} props - Propiedades del componente
 * @param {number} props.longitud - Número total de tabs (7)
 * @param {Array} props.tabsData - Array con los datos de cada tab
 * @param {number} props.currentTab - Índice del tab actualmente activo
 * @param {Function} props.setCurrentTab - Función para cambiar el tab activo
 * @param {number} props.scrollPosition - Posición de scroll horizontal
 * @param {React.RefObject} props.contenedorTabsRef - Referencia al contenedor de tabs
 * @param {Date} props.selectedDate - Fecha seleccionada
 * @param {Function} props.onDateChange - Callback cuando cambia la fecha
 */
const ArrowTabs = React.forwardRef(function ArrowTabs({
  tabsData,
  currentTab,
  setCurrentTab,
  scrollPosition,
  contenedorTabsRef,
  selectedDate,
  onDateChange,
  longitud: propLongitud
}, ref) {
  const dynamicLongitud = propLongitud || longitud;
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [isMobileView, setIsMobileView] = React.useState(window.innerWidth <= CONSTANTS.MOBILE_BREAKPOINT);
  const animationRef = React.useRef(null);

  // Detectar cambios de tamaño de ventana
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= CONSTANTS.MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Configuración óptima del carrusel
  const { needsCarousel, centerIndex } = useMemo(() => {
    const needsCarousel = isMobileView || dynamicLongitud > CONSTANTS.MAX_TABS_WITHOUT_CAROUSEL;
    const centerIndex = Math.floor(dynamicLongitud / 2); // 0-based directo

    return { needsCarousel, centerIndex };
  }, [dynamicLongitud, isMobileView]);

  // Cálculo óptimo de scroll
  const getScrollPosition = useCallback((targetIndex = centerIndex) => {
    const container = contenedorTabsRef.current;
    if (!container || container.scrollWidth <= container.offsetWidth) return 0;

    const tabWidth = container.scrollWidth / dynamicLongitud;
    const containerCenter = container.offsetWidth / 2;
    const tabCenter = (targetIndex * tabWidth) + (tabWidth / 2);

    return Math.max(0, tabCenter - containerCenter);
  }, [centerIndex, dynamicLongitud]);


  // Efecto óptimo para scroll inicial y cuando cambia currentTab
  useEffect(() => {
    if (!contenedorTabsRef.current || !tabsData.length) return;

    const container = contenedorTabsRef.current;
    
    const timeoutId = setTimeout(() => {
      if (!isInitialized) {
        // Scroll inicial
        const initialScroll = getScrollPosition();
        container.scrollLeft = initialScroll;
        setIsInitialized(true);
      } else if (needsCarousel) {
        // Centrar cuando cambia currentTab
        const targetIndex = currentTab - 1;
        const tabWidth = container.scrollWidth / dynamicLongitud;
        const containerCenter = container.offsetWidth / 2;
        const tabCenter = (targetIndex * tabWidth) + (tabWidth / 2);
        const targetScroll = Math.max(0, tabCenter - containerCenter);
        
        container.scrollLeft = targetScroll;
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [isInitialized, needsCarousel, currentTab, dynamicLongitud, tabsData]);


  /**
   * Anima el desplazamiento horizontal del contenedor de tabs
   * @param {number} targetScroll - Posición objetivo del scroll
   */
  const animateScroll = useCallback((targetScroll) => {
    const container = contenedorTabsRef.current;
    if (!container || !isInitialized) return;

    // Cancelar animación anterior
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startScroll = container.scrollLeft;
    const distance = Math.abs(targetScroll - startScroll);

    // Si la distancia es muy pequeña, aplicar directamente
    if (distance < 5) {
      container.scrollLeft = targetScroll;
      return;
    }

    let startTime;
    const step = (time) => {
      if (!startTime) startTime = time;
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / CONSTANTS.ANIMATION_DURATION, 1);

      // Easing suave
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      container.scrollLeft = startScroll + (targetScroll - startScroll) * easeProgress;

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(step);
      } else {
        animationRef.current = null;
      }
    };

    animationRef.current = requestAnimationFrame(step);
  }, [contenedorTabsRef, isInitialized]);

  /**
   * Centra una tab específica en el carrusel
   * @param {number} tabIndex - Índice de la tab a centrar (1-based)
   */
  const centerTab = useCallback((tabIndex) => {
    if (!needsCarousel || !isInitialized) return;

    const targetIndex = tabIndex - 1; // Convertir a 0-based
    const scrollPosition = getScrollPosition(targetIndex);
    animateScroll(scrollPosition);
  }, [needsCarousel, getScrollPosition, animateScroll, isInitialized]);

  // Exponer centerTab a través de ref
  React.useImperativeHandle(ref, () => ({
    centerTab
  }), [centerTab]);

  /**
   * Maneja el cambio de tab activo - SOLO AL HACER CLICK EN TAB
   * @param {number} tabIndex - Índice del tab seleccionado
   */
  const handleTabChange = useCallback((tabIndex) => {
    if (typeof setCurrentTab !== 'function') {
      console.warn('ArrowTabs: setCurrentTab no es una función');
      return;
    }

    setCurrentTab(tabIndex);
  }, [setCurrentTab]);

  /**
   * Maneja el desplazamiento del scroll y cambio de tab en mobile
   * En mobile: cambia la tab activa
   * En desktop: solo desplaza el scroll
   * @param {number} direction - Dirección del scroll (-1 izquierda, 1 derecha)
   */
  const handleScrollOnly = useCallback((direction) => {
    const container = contenedorTabsRef.current;
    if (!container) return;

    // En mobile: cambiar tab activa
    if (isMobileView) {
      const newTab = currentTab + direction;
      // Validar que el nuevo índice esté dentro del rango válido
      if (newTab >= 1 && newTab <= dynamicLongitud) {
        setCurrentTab(newTab);
      }
      return;
    }

    // En desktop: solo desplazar scroll
    const scrollFactor = CONSTANTS.SCROLL_FACTORS.desktop;
    const scrollAmount = container.offsetWidth * scrollFactor;
    const targetScroll = container.scrollLeft + (direction * scrollAmount);

    animateScroll(targetScroll);

  }, [isMobileView, currentTab, dynamicLongitud, setCurrentTab, animateScroll])

  // Memoizar datos de tabs procesados
  const processedTabsData = useMemo(() =>
    Array.from({ length: dynamicLongitud }).map((_, idx) => {
      const tabIndex = idx + 1;
      const data = tabsData[idx];
      const isActive = tabIndex === currentTab;
      const status = isActive ? "Active" : "Inactive";

      return {
        tabIndex,
        data,
        isActive,
        status,
        ariaLabel: `${data?.day || "Día"} ${data?.date || "Fecha"}`
      };
    }), [dynamicLongitud, tabsData, currentTab]
  );

  // Validaciones después de los hooks
  if (!dynamicLongitud || dynamicLongitud <= 0) {
    console.warn('ArrowTabs: longitud debe ser mayor a 0');
    return null;
  }

  if (!Array.isArray(tabsData)) {
    console.warn('ArrowTabs: tabsData debe ser un array');
    return null;
  }

  return (
    <>
      <div className={`container-arrow-tabs ${needsCarousel ? 'with-arrows' : 'without-arrows'}`}>

        {/* Flecha izquierda - mostrar según comportamiento */}
        {needsCarousel && <ArrowButton direction="left" onClick={() => handleScrollOnly(-1)} />}

        {/* Contenedor de tabs */}
        <div
          id="content-tabs"
          className={`content-tabs ${needsCarousel ? 'scrollable' : 'centered'} ${!isInitialized ? 'initializing' : ''}`}
          ref={contenedorTabsRef}
          role="tablist"
          aria-label="Navegación por días"
          style={{
            scrollLeft: !isInitialized && needsCarousel ? getScrollPosition() : undefined
          }}
        >
          {processedTabsData.map(({ tabIndex, data, isActive, status, ariaLabel }) => (
            <div
              key={tabIndex}
              id={`tabContent${tabIndex}`}
              className={`tabContent${status} tabs`}
              onClick={() => handleTabChange(tabIndex)}
              onMouseDown={() => handleTabChange(tabIndex)}
              onTouchStart={() => handleTabChange(tabIndex)}
              role="tab"
              tabIndex={0}
              aria-selected={isActive}
              aria-label={ariaLabel}
              style={{
                pointerEvents: 'auto',
                cursor: 'pointer',
                touchAction: 'manipulation',
                userSelect: 'none'
              }}
            >
              {(() => {
                const formattedDate = formatTabDate(data?.date, data?.day);
                return (
                  <div className="title">{formattedDate.day} <span className="date">{formattedDate.date}</span></div>
                );
              })()}
            </div>
          ))}
        </div>

        {/* Flecha derecha - mostrar según comportamiento */}
        {needsCarousel && <ArrowButton direction="right" onClick={() => handleScrollOnly(1)} />}
      </div>
    </>
  );
});

export default ArrowTabs;